import React, { useState } from 'react';
import { ColorResult, Chrome, color as handleColor } from '@uiw/react-color';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Popper,
  Grow,
  ClickAwayListener,
  Button,
  TextField,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  color: ColorResult | undefined;
  onChange: (color: ColorResult | undefined) => void;
  disabled?: boolean;
};

const ColorInput = ({ color, onChange, disabled }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleColorPickerClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const displayedColor = color?.hsla.a === 1 ? color?.hex : color?.hexa;

  return (
    <>
      <TextField
        sx={{ maxWidth: '260px' }}
        aria-describedby="color-picker"
        disabled={disabled}
        label="Brand Color"
        placeholder="No brand color"
        value={displayedColor ? displayedColor.toUpperCase() : ''}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          sx: { pr: 0, '& input': { cursor: disabled ? 'default' : 'pointer' } },
          readOnly: true,
          onClick: disabled ? undefined : handleColorPickerClick,
          startAdornment: (
            <InputAdornment position="start" disablePointerEvents={disabled}>
              <Button
                sx={{
                  p: 0,
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  background:
                    'repeating-conic-gradient(#ffffff 0 25%, #ebebeb 0 50%) 50% / 16px 16px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ width: '100%', height: '100%', backgroundColor: color?.hexa }} />
              </Button>
            </InputAdornment>
          ),
          endAdornment: color ? (
            <InputAdornment position="end">
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  aria-label="delete"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(undefined);
                  }}
                  disabled={disabled}
                  data-testid="delete-color"
                  sx={{ height: 'fit-content' }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
                {color && (
                  <Button
                    variant="text"
                    disabled={disabled}
                    sx={{
                      borderLeft: '1px solid',
                      borderColor: 'grey.400',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 0,
                      borderRadius: 0,
                      color: 'text.primary',
                      width: '55px',
                    }}
                  >{`${Math.round(color.hsva.a * 100)}%`}</Button>
                )}
              </Stack>
            </InputAdornment>
          ) : null,
        }}
      />
      <Popper
        id="color-picker"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }} timeout={200}>
              <Paper elevation={8} sx={{ overflow: 'hidden', bgcolor: 'background.paper' }}>
                <Chrome color={color?.hsva ?? handleColor('#FFFFFF').hsva} onChange={onChange} />
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default ColorInput;
